import { Region } from '@monthio/react-components'
import { ClientInputs, ClientInputsType } from './clientInputs'

export type ConsentDto = {
	id: string
	isOptional: boolean
	consentTranslations: ConsentTranslation[]
}

export type ConsentTranslation = {
	header: string
	text: string
	languageCode: string
}

export type SmartCheckConfigurationDto = {
	id: string
	redirectOptions: {
		url: string | null
		shouldRedirectWithoutDelay: boolean
	}
	closeTabOptions: {
		shouldAutoCloseTabWithoutDelay: boolean
	}
	shouldHideNavigationBar: boolean
	shouldShowConsumerNote: boolean
	shouldAllowOutputPdfDownload: boolean
	shouldAllowRedirectToNextSession: boolean
	shouldUseCustomColorsInFlow: boolean
	shouldUseBudgetHelper: boolean
	shouldHideAssetBudgets: boolean
	shouldShowIndicationOfResult: boolean
	shouldInputCooperativeHomeValue: boolean
	shouldAllowFileUpload: boolean
	shouldHideDiscretionaryIncome: boolean
	shouldUseNewBudgetsFeature: boolean
	primaryTranslationNamespace: string
	customColors: CustomColors | null
	logo?: string
	hasExplicitConsent: boolean
	consents: ConsentDto[]
	moduleConfiguration: ModuleConfigurationDto
	idDocumentNationalityExemptions: string[] | null
	legalBrandName?: string
	pensionsInfoMinAge?: number
}

export interface CustomColors {
	primaryColor?: string
	secondaryColor?: string
}

export const ProcessingStatuses = {
	Unopened: 'unopened',
	Opened: 'opened',
	Accepted: 'accepted',
	Rejected: 'rejected',
	AutoAccepted: 'autoAccepted',
	AutoRejected: 'autoRejected',
	Cancelled: 'cancelled',
	DocumentationRequested: 'documentationRequested',
	DocumentationReceived: 'documentationReceived',
} as const
export type ProcessingStatus =
	(typeof ProcessingStatuses)[keyof typeof ProcessingStatuses]

export type ProcessingStatusDto = {
	status: ProcessingStatus
}

export type CaseSummaryResponseDto = {
	id: string
	mainApplicantId: string
	processingStatuses: ProcessingStatusDto[]
	calculationReferenceDate: Date | null
}

export type AuthenticateSmartcheckSessionDto = {
	token: string
	consumerId: string
	client: {
		id: number
		name?: string
	}
	clientInputs: ClientInputsDto
	applicantSessionId: string
	finishedOn: Date | null
	smartCheckConfiguration: SmartCheckConfigurationDto
	isReadyToStart: boolean
	isFinalFlow: boolean
	partOfMultiFlow: boolean
	shouldAutoCloseTabOnFlowCompletion: boolean
	questionnaireStatus: QuestionnaireStep[]
	regionId: Region
	case: CaseSummaryResponseDto
	usesApplicantDomainFlow: boolean
	applicantProvidedDocumentConfigurations: readonly ApplicantProvidedDocumentConfiguration[]
}

export interface ApplicantProvidedDocumentConfiguration {
	applicantProvidedDocumentType: ApplicantProvidedDocumentType
	required: boolean
}

export enum ApplicantProvidedDocumentType {
	Other = 'other',
	TaxStatement = 'taxStatement',
	EmploymentProof = 'employmentProof',
	Payslip = 'payslip',
	BalanceSheet = 'balanceSheet',
}

export type QuestionnaireStep = {
	type: string
	answered: boolean
	shouldSkip: boolean
	moduleDefinitionType: ModuleDefinitionType
}

export type FormStatus = {
	answered: boolean
	moduleDefinitionType: ModuleDefinitionType
	shouldSkip: boolean
}

export type BudgetDefinitionSettings = {
	canParringBeSynchronous: boolean
	hasSplashScreen: boolean
	modules: SmartCheckModule[]
}

export type SmartCheckModule = {
	type: SmartCheckModuleType
	settings: any
}

export enum SmartCheckModuleType {
	Bank = 'bank',
	Eskat = 'eskat',
	Pension = 'pension',
	Budget = 'budget',
	DebtOverview = 'debtOverview',
	AssetOverview = 'assetOverview',
	AssetExpenses = 'assetExpenses',
	Tinglysning = 'tinglysning',
	Questionnaire = 'questionnaire',
	Cpr = 'cpr',
	IncomeVerification = 'incomeVerification',
	BudgetConsumerEmail = 'budgetConsumerEmail',
	InsuranceClarification = 'insuranceClarification',
}

export type ModuleConfigurationDto = {
	moduleIds: ModuleDefinitionType[]
	budgetType: BudgetType
}

export enum BudgetType {
	V1 = 'v1',
	V2 = 'v2',
	V2LastYearAverages = 'v2LastYearAverages',
}

export type ModuleDefinitionType =
	(typeof ModuleDefinition)[keyof typeof ModuleDefinition]

export const ModuleDefinition = {
	LandingPage: 'landingPage',
	PersonalInformationQuestionnaire: 'personalInformationQuestionnaire',
	PersonalInformationExtendedQuestionnaire:
		'personalInformationExtendedQuestionnaire',
	PersonalInformationCprOnlyQuestionnaire:
		'personalInformationCprOnlyQuestionnaire',
	HouseholdQuestionnaire: 'householdQuestionnaire',
	HouseholdSingleCohabitantQuestionnaire:
		'householdSingleCohabitantQuestionnaire',
	AssetQuestionnaire: 'assetQuestionnaire',
	CarExtendedQuestionnaire: 'carExtendedQuestionnaire',
	MbfQuestionnaire: 'mbfQuestionnaire',
	Eskat: 'eskat',
	Bank: 'bank',
	DebtOverview: 'debtOverview',
	AssetOverview: 'assetOverview',
	AssetExpenses: 'assetExpenses',
	Budget: 'budget',
	Cpr: 'cpr',
	Tinglysning: 'tinglysning',
	IncomeVerification: 'incomeVerification',
	InsuranceClarification: 'insuranceClarification',
	BudgetConsumerEmail: 'budgetConsumerEmail',
	Pension: 'pension',
	AssetReplacement: 'assetReplacement',

	FlowStarted: 'flowStarted',
	SamsungBrowserWarning: 'samsungBrowserWarning',
	ClientConsent: 'clientConsent',
	Questionnaire: 'questionnaire',
	PersonalQuestionnaire: 'personalQuestionnaire',
	FlowCompletion: 'flowCompletion',
	DocumentUpload: 'documentUpload',
	Finished: 'finished',
	Deleted: 'deleted',
	CoApplicantNotCompleted: 'coApplicantNotCompleted',
	SavingsCapacity: 'savingsCapacity',
	CustomQuestionnaire: 'customQuestionnaire',
	AccountVerification: 'accountVerification',
} as const

export const isQuestionnaireModuleDefinition = (
	moduleDefinition: ModuleDefinitionType,
) => {
	switch (moduleDefinition) {
		case ModuleDefinition.AssetQuestionnaire:
		case ModuleDefinition.CarExtendedQuestionnaire:
		case ModuleDefinition.HouseholdQuestionnaire:
		case ModuleDefinition.HouseholdSingleCohabitantQuestionnaire:
		case ModuleDefinition.MbfQuestionnaire:
		case ModuleDefinition.PersonalInformationCprOnlyQuestionnaire:
		case ModuleDefinition.PersonalInformationExtendedQuestionnaire:
		case ModuleDefinition.PersonalInformationQuestionnaire:
			return true

		default:
			return false
	}
}

export interface ClientInputsDto extends ClientInputs {
	type: ClientInputsType
	assets: AssetsDto | null
}

export interface AssetsDto {
	vehicles: AssetVehicleDto[]
}

interface AssetVehicleDto {
	type?: VehicleType
	financingDetails?: FinancingDetailsDto
	assetInfo?: AssetVehicleInfoDto
	expenses?: AssetVehicleExpensesDto
}

enum VehicleType {
	Houseboat = 'houseboat',
	Car = 'car',
	CampingWagon = 'campingWagon',
	Motorcycle = 'motorcycle',
	RV = 'rv',
	Boat = 'boat',
}

interface FinancingDetailsDto {
	price?: number
	downPayment?: number
	interestType?: InterestType
	interestRate?: number
	duration?: number
}

enum InterestType {
	Fixed = 'fixed',
	Variable = 'variable',
}

interface AssetVehicleInfoDto {
	brand?: string
	model?: string
	propellant?: PropellantType
	registrationNumber?: string
}

enum PropellantType {
	Electricity = 'electricity',
	Gas = 'gas',
	Hydrogen = 'hydrogen',
	Diesel = 'diesel',
	None = 'none',
}

interface AssetVehicleExpensesDto {
	loan?: number
	leasing?: number
	propellant?: number
	vehicleFees?: number
	maintenance?: number
}
